import { ConfirmationDialog } from '@components/ConfirmationDialog';
import { useReadFilesAsBase64 } from '@components/FilesSelector/hooks/useReadFilesAsBase64';
import { useEditorParseData } from '@components/FormulaEditor';
import { ModalDialog } from '@components/ModalDialog';
import { UploadStatus } from '@components/UploadStatus';
import type { IErrorHandlingRequestState } from '@features/errorHandling/errorHandlingSlice';
import { RequestStatus } from '@features/request/requestSlice';
import type { CatalogLimitedCostTypeData, ConstructionTypeData, GlobalLimitedCostData } from '@gql/graphql';
import { ChapterEnum } from '@gql/graphql';
import { useMethodsConfirmationDialog } from '@hooks/useMethodsConfirmationDialog';
import { useProjectData } from '@hooks/useProjectData';
import { Loader } from '@uikit';
import { readFileFromBase64 } from '@utils/readFileFromBase64';
import { replacerCost } from '@utils/replacerCost';
import type { CalculatingParticipationItem } from 'pages/Project/UerProject/Calculation/commonData';
import { CALCULATING_PARTICIPATION_VARIANTS, requiredMessage } from 'pages/Project/UerProject/Calculation/commonData';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getNumberFormatter } from '../../../../Common/utils';
import type { ChapterItem, TabItem } from '../commonData';
import { CHAPTER_ITEMS, CreatingTypes, tabsItems } from '../commonData';
import { useBimCalculationLimitedCostForUpdate } from '../hooks/useBimCalculationLimitedCostForUpdate';
import { useBimCreateCalculationLimitedCost } from '../hooks/useBimCreateCalculationLimitedCost';
import { useBimIsUniqueKeyCalculationLimitedCost } from '../hooks/useBimIsUniqueKeyCalculationLimitedCost';
import { useBimIsUniqueNameLimitedCost } from '../hooks/useBimIsUniqueNameLimitedCost';
import { useBimUpdateCalculationLimitedCost } from '../hooks/useBimUpdateCalculationLimitedCost';

import { BimCalculationLimitedEditForm } from './BimCalculationLimitedEditForm';
import { useBimGetCalculationLimitedCostTypes } from './BimLimitedCostTypeSelect/hooks/useBimGetCalculationLimitedCostTypes';

interface IBimCalculationLimitedEditModal {
    isCreateModal?: boolean;
    isEditModal?: boolean;
    handleClose: (isSuccess?: boolean) => void;
    creatingType?: CreatingTypes;
    isAdditionalTransportProp?: boolean;
    constructionTypeId: number;
    reloadHandler?: (id: number) => void;
    constructionTypes?: ConstructionTypeData[];
    calculationId?: number;
    limitedIdProp?: number;
}

export interface IBimLimitedCostFormData {
    name?: string;
    key?: string;
    calculatingType?: string;
    calculatingTypeValue?: number;
    globalOtherCostId?: number;
    chapter?: ChapterEnum;
    catalogLimitedCostTypeId?: string;
    formula: string;
    cost?: { [key: string]: number };
    uniqueId: string;
    limitedCostType: CatalogLimitedCostTypeData;
    globalLimitedCost: GlobalLimitedCostData;
    hasCalculatingParticipation: CalculatingParticipationItem;
    rationale: string;
    globalLimitedCostId?: number;
    isNorthernAllowance?: boolean;
    isManualLimitsDistribution?: boolean;
}

export const BimCalculationLimitedEditModal: React.FC<IBimCalculationLimitedEditModal> = ({
    isCreateModal,
    isEditModal,
    handleClose,
    creatingType,
    constructionTypeId,
    reloadHandler,
    constructionTypes,
    calculationId,
    limitedIdProp,
    isAdditionalTransportProp,
}) => {
    const dispatch = useDispatch();

    const { id: projectId } = useParams();
    const { refreshProject } = useProjectData(projectId, !limitedIdProp);

    const [activeTab, setActiveTab] = useState<TabItem>(tabsItems[0]);
    const [selectedChapter, setSelectedChapter] = useState<ChapterItem>(
        isAdditionalTransportProp ? CHAPTER_ITEMS[2] : CHAPTER_ITEMS[0],
    );
    const [selectedConstructionTypes, setSelectedConstructionTypes] = useState<Record<string, boolean>>({
        [constructionTypeId]: true,
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [preparedData, setPreparedData] = useState<IBimLimitedCostFormData>({} as IBimLimitedCostFormData);
    const [noLimitedCostTypeErrorState, setNoLimitedCostTypeErrorState] = useState<
        IErrorHandlingRequestState | undefined
    >();
    const [files, setFiles] = useState<File[]>([]);

    const form = useForm({ shouldUnregister: false });
    const {
        control,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        getValues,
        formState: { isDirty },
    } = form;

    const title = useMemo(() => {
        if (isAdditionalTransportProp) {
            return isCreateModal
                ? 'Создание дополнительной транспортировки'
                : 'Редактирование дополнительной транспортировки';
        }

        if (isCreateModal) {
            return 'Создание лимитированных затрат';
        }
        if (isEditModal) {
            return 'Редактирование лимитированных затрат';
        }

        return 'Просмотр лимитированных затрат';
    }, [isAdditionalTransportProp, isCreateModal, isEditModal]);

    const btnConfirmLabel = useMemo(() => {
        if (isCreateModal) {
            return 'Создать';
        }
        if (isEditModal) {
            return 'Сохранить';
        }

        return '';
    }, [isCreateModal, isEditModal]);

    const { limitedForUpdateData, limitedForUpdateReset, limitedForUpdateStatus } =
        useBimCalculationLimitedCostForUpdate({
            limitedCostId: limitedIdProp || undefined,
            calculationId,
        });
    const isDataFetching = useMemo(() => limitedForUpdateStatus === RequestStatus.FETCHING, [limitedForUpdateStatus]);

    const isOtherFromCatalog = useMemo(
        () => !!limitedForUpdateData?.globalLimitedCostId,
        [limitedForUpdateData?.globalLimitedCostId],
    );

    const currentCreatingType = useMemo(
        () => creatingType ?? (isOtherFromCatalog ? CreatingTypes.BOOK : CreatingTypes.MANUAL),
        [creatingType, isOtherFromCatalog],
    );
    const globalLimitedCost: GlobalLimitedCostData = useWatch({ control, name: 'globalLimitedCost' });

    const handleTabChange = useCallback(({ value }: { value: TabItem }) => setActiveTab(value), [setActiveTab]);
    const onSelectedChapterChange = useCallback(({ value }: { value: ChapterItem }) => setSelectedChapter(value), []);

    const constructionTypeCheckHandler = useCallback((id, state) => {
        setSelectedConstructionTypes((types) => ({
            ...types,
            [id]: state,
        }));
    }, []);

    const { items } = useBimGetCalculationLimitedCostTypes({
        calculationId,
    });

    const onNoLimitedCostTypeErrorsClose = useCallback(() => {
        setNoLimitedCostTypeErrorState(undefined);
        setValue('globalLimitedCostId', undefined);
    }, [setValue]);

    const { deserializeFromBackend, serializeStringForBackend } = useEditorParseData({});

    useEffect(() => {
        if (globalLimitedCost?.chapter) {
            setSelectedChapter(CHAPTER_ITEMS.find(({ id }) => id === globalLimitedCost?.chapter) as ChapterItem);
        }
        setValue(
            'hasCalculatingParticipation',
            CALCULATING_PARTICIPATION_VARIANTS.find(({ id }) => id === globalLimitedCost?.hasCalculatingParticipation),
        );
        setValue('formula', deserializeFromBackend(globalLimitedCost?.formula));
        setValue('limitedCostType', globalLimitedCost?.catalogLimitedCostType);
    }, [
        deserializeFromBackend,
        globalLimitedCost?.catalogLimitedCostType,
        globalLimitedCost?.chapter,
        globalLimitedCost?.formula,
        globalLimitedCost?.hasCalculatingParticipation,
        setValue,
    ]);

    useEffect(() => {
        if (globalLimitedCost?.catalogLimitedCostType?.uniqueId && items) {
            const currentLimitedCostType = items?.find(
                (t) => t.uniqueId === globalLimitedCost?.catalogLimitedCostType?.uniqueId,
            );

            setValue('catalogLimitedCostType', currentLimitedCostType);

            if (!currentLimitedCostType) {
                setNoLimitedCostTypeErrorState({
                    message: `Тип лимитированной "${globalLimitedCost?.catalogLimitedCostType?.name}" используемый в выбранной позиции лимитированных затрат "${globalLimitedCost?.name}" из Справочника уже был назначен на другую позицию лимитированных.\r\nИспользование лимитированной из Справочника с данным типом недопустимо`,
                });
            }
        }
    }, [
        globalLimitedCost?.catalogLimitedCostType?.name,
        globalLimitedCost?.catalogLimitedCostType?.uniqueId,
        globalLimitedCost?.name,
        items,
        setValue,
    ]);

    const { isUniqueKey, isUniqueKeyFetching, isUniqueKeyRequest, resetIsUniqueKeyData, isUniqueKeySetData } =
        useBimIsUniqueKeyCalculationLimitedCost();

    const { isUniqueName, isUniqueNameRequestStatus, isUniqueNameRequest, resetIsUniqueNameData, isUniqueNameSetData } =
        useBimIsUniqueNameLimitedCost();

    const {
        createCalculationLimitedCostData,
        createCalculationLimitedCostStatus,
        createCalculationLimitedCostReset,
        createCalculationLimitedCostMakeRequest,
    } = useBimCreateCalculationLimitedCost({
        calculationId: Number(calculationId),
        refreshProject,
        selectedChapter,
    });

    const {
        updateCalculationLimitedCostDataResult,
        updateCalculationLimitedCostStatus,
        updateCalculationLimitedCostReset,
        updateCalculationLimitedCostMakeRequest,
        bimcUpdateCalculationLimitedCostSetData,
    } = useBimUpdateCalculationLimitedCost({
        calculationId: Number(calculationId),
        refreshProject,
        id: limitedForUpdateData?.id,
        selectedChapter,
    });

    const resetData = useCallback(() => {
        isUniqueKeySetData(null);
        isUniqueNameSetData(null);
        updateCalculationLimitedCostReset(null);
        bimcUpdateCalculationLimitedCostSetData(null);
        createCalculationLimitedCostReset(null);
        limitedForUpdateReset(null);
        reset();
        setFiles([]);
    }, [
        isUniqueKeySetData,
        isUniqueNameSetData,
        updateCalculationLimitedCostReset,
        bimcUpdateCalculationLimitedCostSetData,
        createCalculationLimitedCostReset,
        limitedForUpdateReset,
        reset,
        setFiles,
    ]);

    const closeAndResetModal = useCallback(() => {
        resetData();
        handleClose?.();
        setIsSubmitted(false);
        setPreparedData({} as IBimLimitedCostFormData);
    }, [handleClose, resetData]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: closeAndResetModal,
        });

    useEffect(() => {
        if (createCalculationLimitedCostData?.id && createCalculationLimitedCostStatus !== RequestStatus.FETCHING) {
            const { id } = createCalculationLimitedCostData;

            resetData();
            handleClose?.();
            reloadHandler?.(id);
        }
    }, [
        createCalculationLimitedCostData,
        createCalculationLimitedCostStatus,
        handleClose,
        reloadHandler,
        reset,
        resetData,
    ]);

    useEffect(() => {
        if (limitedForUpdateData) {
            const data = limitedForUpdateData || {};

            const chapter = (data?.chapter && CHAPTER_ITEMS.find(({ id }) => id === data.chapter)) || CHAPTER_ITEMS[0];
            const formula = data && 'formula' in data ? deserializeFromBackend(data?.formula) : '';

            setSelectedChapter(chapter);

            setSelectedConstructionTypes(
                limitedForUpdateData?.groupConstructionLimits?.reduce((acc: Record<string, boolean>, item) => {
                    acc[item.constructionTypeId] = true;

                    return acc;
                }, {}) || {},
            );

            const hasCalculatingParticipation =
                CALCULATING_PARTICIPATION_VARIANTS.find(
                    ({ id }) => id === limitedForUpdateData?.hasCalculatingParticipation,
                ) || CALCULATING_PARTICIPATION_VARIANTS[0];

            const costs = limitedForUpdateData?.groupConstructionLimits?.reduce((acc: number[], item) => {
                acc[item.constructionTypeId] = item.calculatingValue;

                return acc;
            }, []);

            if (limitedForUpdateData?.file) {
                const file = readFileFromBase64(limitedForUpdateData.file.dataUrl, limitedForUpdateData.file.name);

                setFiles(file ? [file] : []);
            }

            reset({
                id: limitedForUpdateData?.id,
                uniqueId: limitedForUpdateData?.uniqueId,
                name: limitedForUpdateData?.name,
                key: limitedForUpdateData?.key,
                formula,
                hasCalculatingParticipation,
                catalogLimitedCostTypeId: limitedForUpdateData?.catalogLimitedCostTypeId,
                limitedCostType: limitedForUpdateData?.catalogLimitedCostType,
                globalLimitedCostId: limitedForUpdateData?.globalLimitedCostId,
                isNorthernAllowance: limitedForUpdateData?.isNorthernAllowance,
                isManualLimitsDistribution: limitedForUpdateData?.isManualLimitsDistribution,
                cost: costs,
                rationale: limitedForUpdateData?.rationale,
                percent:
                    costs && Object.values(costs)?.length
                        ? getNumberFormatter(3)({ value: Object.values(costs)[0] })
                        : null,
            });
        }
    }, [deserializeFromBackend, limitedForUpdateData, reset]);

    const isAdditionalTransport = useMemo(
        () => isAdditionalTransportProp || selectedChapter?.id === ChapterEnum.Chapter1_7,
        [isAdditionalTransportProp, selectedChapter?.id],
    );

    const onCancel = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            closeAndResetModal();
        }
    }, [closeAndResetModal, handleOpenConfirmationDialog, isDirty]);

    const isUpdating = useMemo(
        () =>
            updateCalculationLimitedCostStatus === RequestStatus.FETCHING ||
            createCalculationLimitedCostStatus === RequestStatus.FETCHING,
        [createCalculationLimitedCostStatus, updateCalculationLimitedCostStatus],
    );

    useEffect(() => {
        if (
            isSubmitted &&
            (updateCalculationLimitedCostDataResult === true || createCalculationLimitedCostData?.result === true) &&
            !isUpdating
        ) {
            resetData();
            setPreparedData({} as IBimLimitedCostFormData);
            setIsSubmitted(false);
            handleClose?.(true);
        }
    }, [
        createCalculationLimitedCostData,
        createCalculationLimitedCostData?.result,
        handleClose,
        isSubmitted,
        isUpdating,
        resetData,
        updateCalculationLimitedCostDataResult,
    ]);

    const base64Files = useReadFilesAsBase64(files);

    const onSubmitContinue = useCallback(
        (_data: IBimLimitedCostFormData) => {
            const constructionTypesIds = Object.entries(selectedConstructionTypes)
                .filter(([, value]) => value)
                .map(([id, value]: [id: string, value: boolean]) => ({
                    constructionTypeId: Number(id),
                    calculatingValue: replacerCost(_data?.cost?.[id as string]),
                }));

            if (isCreateModal) {
                createCalculationLimitedCostMakeRequest(
                    _data,
                    constructionTypesIds,
                    serializeStringForBackend(_data?.formula),
                    base64Files,
                );
            }
            if (isEditModal) {
                updateCalculationLimitedCostMakeRequest(
                    _data,
                    constructionTypesIds,
                    serializeStringForBackend(_data.formula),
                    base64Files,
                );
            }
        },
        [
            selectedConstructionTypes,
            isCreateModal,
            isEditModal,
            createCalculationLimitedCostMakeRequest,
            serializeStringForBackend,
            base64Files,
            updateCalculationLimitedCostMakeRequest,
        ],
    );

    useEffect(() => {
        if (isSubmitted && isUniqueKeyFetching === RequestStatus.SUCCESS) {
            if (isUniqueKey) {
                clearErrors('key');
            } else {
                setActiveTab(tabsItems[0]);
                setError('key', { message: 'Лимитированные затраты с таким кодом уже существуют' });
            }
        } else if (isUniqueKeyFetching === RequestStatus.ERROR) {
            clearErrors('key');
        }
    }, [clearErrors, isSubmitted, isUniqueKey, isUniqueKeyFetching, setError]);

    useEffect(() => {
        if (isSubmitted && isUniqueNameRequestStatus === RequestStatus.SUCCESS) {
            if (isUniqueName) {
                clearErrors('name');
            } else {
                setActiveTab(tabsItems[0]);
                setError('name', { message: 'Лимитированная с таким наименованием уже существует' });
            }
        } else if (isUniqueNameRequestStatus === RequestStatus.ERROR) {
            clearErrors('name');
        }
    }, [setError, clearErrors, isSubmitted, isUniqueNameRequestStatus, isUniqueName]);

    useEffect(() => {
        if (isUniqueName && (isUniqueKey || !preparedData.key) && !isUpdating && Object.keys(preparedData).length > 0) {
            resetIsUniqueKeyData(null);
            resetIsUniqueNameData(null);
            onSubmitContinue(preparedData);
        }
    }, [
        isUniqueKey,
        isUniqueName,
        onSubmitContinue,
        preparedData,
        resetIsUniqueKeyData,
        resetIsUniqueNameData,
        isUpdating,
    ]);

    const onSubmit = useCallback(
        (data) => {
            if (!data || Object.keys(data).length === 0) {
                return;
            }

            if (isUpdating) {
                return;
            }

            const hasName = Object.prototype.hasOwnProperty.call(data, 'name');

            if (
                (hasName && (data.name === undefined || data.name === null)) ||
                (!hasName && (data.globalLimitedCost === undefined || data.globalLimitedCost === null))
            ) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => {
                    setError('name', requiredMessage);
                    setError('globalLimitedCost', requiredMessage);
                }, 0);

                return;
            }

            setPreparedData(data);

            if (data.key) {
                dispatch(
                    isUniqueKeyRequest({
                        calculationId: calculationId as number,
                        limitedCostId: data.id,
                        key: data.key,
                    }),
                );
            }

            if (hasName || !(data.globalLimitedCost === undefined || data.globalLimitedCost === null)) {
                dispatch(
                    isUniqueNameRequest({
                        calculationId: calculationId as number,
                        limitedCostId: data.id,
                        name: data.name || data.globalLimitedCost.name,
                    }),
                );
            }

            setIsSubmitted(true);
        },
        [calculationId, dispatch, isUniqueKeyRequest, isUniqueNameRequest, setError, isUpdating],
    );

    return (
        <>
            {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}
            <ModalDialog
                title={title}
                isOpen
                hasOverlay
                withTitleCloseButton
                onClose={onCancel}
                onConfirm={!isCreateModal && !isEditModal ? undefined : handleSubmit(onSubmit)}
                isCommit={isUpdating}
                btnConfirmLabel={btnConfirmLabel}
                btnCancelLabel={!isCreateModal && !isEditModal ? 'Закрыть' : undefined}
                btnCancelView={!isCreateModal && !isEditModal ? 'primary' : undefined}
                style={{ width: 644 }}
                isOnClickOutsideDisable
            >
                <FormProvider {...form}>
                    <BimCalculationLimitedEditForm
                        activeTab={activeTab}
                        calculationId={calculationId}
                        selectedChapter={selectedChapter}
                        handleTabChange={handleTabChange}
                        onSelectedChapterChange={onSelectedChapterChange}
                        control={control}
                        creatingType={currentCreatingType}
                        isAdditionalTransport={isAdditionalTransport}
                        handleChangeConstructionType={constructionTypeCheckHandler}
                        globalLimitedCost={globalLimitedCost}
                        globalOtherName={getValues('name')}
                        selectedConstructionTypes={selectedConstructionTypes}
                        constructionTypes={constructionTypes}
                        currentConstructionTypeId={constructionTypeId}
                        setValue={setValue}
                        getValues={getValues}
                        clearErrors={clearErrors}
                        isCreateModal={isCreateModal}
                        isEditModal={isEditModal}
                        isViewModal={!isCreateModal && !isEditModal}
                        files={files}
                        setFiles={setFiles}
                    />
                </FormProvider>

                <ConfirmationDialog
                    textHeader="Отмена действия"
                    textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                    cancelButtonText="Не отменять"
                    confirmButtonText="Отменить"
                    isOpened={isOpenConfirmationDialog}
                    onSubmit={handleActionQuery}
                    onClose={handleCloseConfirmationDialog}
                />
                <UploadStatus
                    isOpened={!!noLimitedCostTypeErrorState}
                    handleCloseModal={onNoLimitedCostTypeErrorsClose}
                    data={noLimitedCostTypeErrorState}
                    titleText={title}
                    status="error"
                    btnText="Понятно"
                />
            </ModalDialog>
        </>
    );
};

BimCalculationLimitedEditModal.displayName = 'BimCalculationLimitedEditModal';
